import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { get, merge, omit, isEmpty, startCase, range, includes } from 'lodash';
import { Field, Form, Formik } from 'formik';
import PropTypes from 'prop-types';
import * as Yup from 'yup';
import { MessageBar, MessageBarType } from 'office-ui-fabric-react/lib/MessageBar';
import { useLocation } from 'react-router-dom';
import {
  DefaultButton,
  Dialog,
  DialogFooter,
  DialogType,
  PrimaryButton,
} from 'office-ui-fabric-react';
import { useBoolean } from '@uifabric/react-hooks';
import { createContract, editContract, fetchContract } from '../../actions/contracts';
import { createPartner, fetchPartners } from '../../actions/partners';
import { fetchAssignees } from '../../actions/assignees';
import StackGrid from '../StackGrid';
import SubModule from '../SubModule';
import SubModuleHeader from '../SubModuleHeader';
import SubModuleContent from '../SubModuleContent';
import Dropdown from '../Dropdown';
import TextField from '../TextField';
import DatePicker from '../DatePicker';
import {
  companyCodes,
  criticalFields,
  purchaseClassificationCodes,
  silos,
  statementFrequency,
  taxCodes,
} from '../../constants';
import { fetchDigitizedContract } from '../../actions/digitizedContracts';
import { addTimeToDate, isFieldLengthTooLong, normalizeDate, removePunctuation } from '../../utils';
import { createDatePickerProps } from '../DynamicDetailsList/DateColumns';
import PartnerTypeAhead from '../PartnerTypeAhead/PartnerTypeAhead';
import ContractDateSelectionDialog from './ContractDateSelectionDialog';
import CurrencyTypeAhead from '../CurrencyTypeAhead';
import styles from './ContractTermsForm.style';

const validationSchema = Yup.object().shape({
  name: Yup.string().required('Required').label('Name'),
  silo: Yup.string().required('Required'),
  partnerAddress: Yup.string().required('Required'),
  effectiveDate: Yup.date().required('Required'),
  terminationDate: Yup.date()
    .min(Yup.ref('effectiveDate'), 'Expiration Date must be after Effective Date')
    .required('Required'),
  contractCurrency: Yup.string().required('Required'),
  contractId: Yup.string().required('Required'),
  perCapDays: Yup.number(),
  perCapPercentage: Yup.number()
    .min(0, 'Per Cap Percentage must be between 0 and 100')
    .max(100, 'Per Cap Percentage must be between 0 and 100'),
  metadata: Yup.object()
    .shape({
      paymentCurrency: Yup.string().required('Required'),
      paymentTerms: Yup.number().required('Required').label('Payment Terms'),
      paymentStartDate: Yup.date().required('Required'),
      paymentMinimum: Yup.number().min(0).required('Required').label('Payment Minimum'),
      statementFrequency: Yup.string().required('Required'),
      firstStatementPeriod: Yup.string().required('Required'),
      purchaseClassificationCode: Yup.string(),
      companyCode: Yup.string(),
      taxCode: Yup.string(),
      auditWindow: Yup.number().required('Required'),
      comment: Yup.string(),
      keywords: Yup.string(),
      businessAccountManager: Yup.string().required('Required'),
      businessProcessingManager: Yup.string().required('Required'),
      royaltyOperationsManager: Yup.string().required('Required'),
      royaltyAnalyst: Yup.string().required('Required'),
      contractProcessingManager: Yup.object()
        .shape({
          oid: Yup.string().required('Required'),
        })
        .required('Required'),
    })
    .required('Required'),
});

const dialogContentProps = {
  type: DialogType.normal,
  title: 'Pausing Contract',
  subText: 'This action will pause the contract. Are you sure you would like to continue?',
};

function extractAttributes(attributesList) {
  const partnerName = removePunctuation(attributesList['Publisher Name'].valueString);
  const contractSilo = attributesList['Agreement Type'].valueString;
  const effectiveDate = normalizeDate(attributesList['Effective Date'].valueString);
  const firstStatementPeriod = normalizeDate(attributesList['First Statement Period'].valueString);
  const frequency = attributesList['Payment Frequency'].valueString.split(' ')[0];
  const expirationDate = addTimeToDate(effectiveDate, attributesList.Term.valueString);
  const audit = `${attributesList.Audit.valueString.substring(
    0,
    attributesList.Audit.valueString.indexOf(' '),
  )}`;
  const percap = attributesList.Percap.valueString;
  const percapDays = attributesList['Percap Days'].valueString;
  return {
    contractSilo,
    partnerName,
    effectiveDate,
    frequency,
    firstStatementPeriod,
    expirationDate,
    audit,
    percap,
    percapDays,
  };
}

export const getQuarterStartMonths = date => {
  const quarterRange = range(1, 5);
  return quarterRange.map(quarter => moment(moment(date).quarter(quarter)).month());
};

const ContractTermsForm = React.forwardRef(
  ({ address, onSubmit, onSuccess, onFailure, reviewMode }, ref) => {
    const [formData, setFormData] = useState({});
    const [dialogHidden, { setTrue: hideDialog, setFalse: showDialog }] = useBoolean();
    const [critTouched, setCritTouched] = useState(false);
    const [formAttributes, setFormAttributes] = useState({});
    const [partnerAddress, setPartnerAddress] = useState('');
    const [isExistingPartner, setIsExistingPartner] = useState(true);
    const [showDateSelectionDialog, setShowDateSelectionDialog] = useState(false);
    const [currencyData, setCurrencyData] = useState({});
    const [currentCurrencyDate, setCurrentCurrencyDate] = useState(null);
    const dispatch = useDispatch();
    const location = useLocation();
    const query = new URLSearchParams(location.search);
    const contractId = query.get('contractId');
    const assignees = useSelector(state => Object.values(get(state, 'entities.assignees', {})));
    const contract = useSelector(state => get(state, `entities.contracts.${address}`, null));
    const hasTiering = useSelector(
      state =>
        contract?.agreements?.some(a => get(state, `entities.agreements.${a}`).tieringMethod) ??
        false,
    );
    const partners = useSelector(state => Object.values(get(state, 'entities.partners', {}))).map(
      partner => ({
        name: partner.name,
        key: partner.partnerAddress,
        text: partner.name,
      }),
    );
    const [contractCurrency, setContractCurrency] = useState({
      key: contract ? get(contract, 'contractCurrency', '') : 'USD',
      text: contract ? get(contract, 'contractCurrency', '') : 'USD',
    });
    const [paymentCurrency, setPaymentCurrency] = useState({
      key: contract ? get(contract, 'metadata.paymentCurrency', '') : 'USD',
      text: contract ? get(contract, 'metadata.paymentCurrency', '') : 'USD',
    });
    const [tempCurrency, setTempCurrency] = useState({});

    const digitizedContract = useSelector(state =>
      get(state, `entities.digitizedcontracts.${contractId}`, null),
    );
    const selectedPartner = contract?.partner
      ? { key: contract.partner.address, text: contract.partner.name }
      : null;

    function filterContractAssignees(roleType) {
      return assignees
        .filter(assignee => assignee.secondaryRoles.map(role => role.slug).includes(roleType))
        .map(assignee => ({
          key: assignee.oid,
          text: assignee.displayName,
        }));
    }

    const royaltyAnalysts = filterContractAssignees('royaltyAnalyst');
    const businessAccountManagers = filterContractAssignees('businessAccountManager');
    const businessProcessingManagers = filterContractAssignees('businessProcessingManager');
    const royaltyOperationsManagers = filterContractAssignees('royaltyOperationsManager');
    const contractProcessingManagers = filterContractAssignees('contractProcessingManager');

    useEffect(() => {
      dispatch(fetchPartners());
    }, [dispatch]);

    useEffect(() => {
      if (contractId) {
        dispatch(fetchDigitizedContract(contractId));
      }
    }, [dispatch, contractId]);

    useEffect(() => {
      dispatch(fetchAssignees());
    }, [dispatch]);

    useEffect(() => {
      if (address) {
        dispatch(fetchContract(address, { useCache: false }));
      }
    }, [dispatch, address]);

    const setCurrency = (currency, setField) => {
      const key = currency?.key || currency?.id;
      const text = currency?.text ? currency.text : `${key} - ${currency?.name}`;
      setField({ key, text });
    };

    const findCurrencyState = field => {
      return includes(field, 'contractCurrency') ? setContractCurrency : setPaymentCurrency;
    };

    const getCurrencyStateValue = field => {
      return includes(field, 'contractCurrency') ? contractCurrency : paymentCurrency;
    };

    useEffect(() => {
      if (formAttributes.partnerName && !isExistingPartner) {
        const generatedAddress = '';
        const partnerData = {
          partnerAddress: generatedAddress,
          name: formAttributes.partnerName,
          partnerRole: formAttributes.partnerName.split(' ')[0].toLowerCase(),
        };
        dispatch(createPartner(partnerData));
      }
    }, [dispatch, isExistingPartner, formAttributes.partnerName]);

    useEffect(() => {
      const getPartnerAddress = partnerName => {
        const curPartners = partners.filter(partner => {
          return removePunctuation(partner.name) === partnerName;
        });

        let curPartnerAddress;
        // key object cannot be accessed directly.
        curPartners.forEach(partner => {
          curPartnerAddress = partner.key;
        });

        return curPartnerAddress;
      };

      if (partners.length !== 0 && !formAttributes.partnerAddress && formAttributes.partnerName) {
        const curPartnerAddress = getPartnerAddress(formAttributes.partnerName);
        if (curPartnerAddress) {
          setPartnerAddress(curPartnerAddress);
        }
      }
    }, [partners, formAttributes.partnerAddress, formAttributes.partnerName]);

    const attachments =
      digitizedContract && digitizedContract.documentLocation
        ? [
            {
              documentLocation: digitizedContract.documentLocation,
              documentName: digitizedContract.documentName,
              documentType: 'contract',
            },
          ]
        : undefined;

    function mapAssigneeToOid(name) {
      const user = assignees.find(assignee => assignee.displayName.includes(name));
      return user?.oid;
    }

    const contractAssignments = digitizedContract
      ? {
          businessAccountManager: {
            oid: mapAssigneeToOid(digitizedContract.businessAccountManager),
          },
          businessProcessingManager: {
            oid: mapAssigneeToOid(digitizedContract.businessProcessingManager),
          },
          royaltyOperationsManager: {
            oid: mapAssigneeToOid(digitizedContract.royaltyOperationsManager),
          },
          royaltyAnalyst: {
            oid: mapAssigneeToOid(digitizedContract.royaltyAnalyst),
          },
          contractProcessingManager: {
            oid: mapAssigneeToOid(digitizedContract.contractProcessingManager),
          },
        }
      : undefined;

    const initialValues = omit(
      merge(
        {
          silo: '3p-tla',
          partnerAddress,
          effectiveDate: formAttributes.effectiveDate || '',
          terminationDate: formAttributes.expirationDate || '',
          contractCurrency: 'USD',
          contractId: contractId || '',
          name: formAttributes.contractName || '',
          perCapDays: formAttributes.percapDays || '',
          perCapPercentage: formAttributes.percap || '',
          metadata: {
            paymentCurrency: 'USD',
            paymentTerms: formAttributes.frequency || '',
            paymentStartDate: formAttributes.effectiveDate || '',
            paymentMinimum: '200',
            purchaseClassificationCode: '',
            statementFrequency: 'month',
            firstStatementPeriod: formAttributes.effectiveDate || '',
            companyCode: '',
            taxCode: '',
            auditWindow: formAttributes.audit || '',
            comment: '',
            keywords: '',
            attachments,
            vendorId: '',
            ...contractAssignments,
          },
        },
        contract,
      ),
      // remove contract address & attachments so it doesn't get sent in request body
      ['address', 'attachments'],
    );

    if (contractId && digitizedContract && isEmpty(formAttributes)) {
      const { attributes } = digitizedContract;
      let contractName = removePunctuation(digitizedContract.contractName);
      contractName = isFieldLengthTooLong(contractName, 32)
        ? `${contractName.substring(0, 19).trim()} Xbox One TLA`
        : contractName;

      const extractedAttributes = extractAttributes(attributes);
      const {
        partnerName,
        contractSilo,
        effectiveDate,
        frequency,
        firstStatementPeriod,
        expirationDate,
        audit,
        percap,
        percapDays,
      } = extractedAttributes;

      setFormAttributes({
        partnerName,
        contractSilo,
        effectiveDate,
        frequency,
        firstStatementPeriod,
        expirationDate,
        audit,
        percap,
        percapDays,
        contractName,
      });

      setIsExistingPartner(partners.some(partner => partner.name === partnerName));
    }

    const submitToApi = async (submittedValues, setErrors) => {
      let response;
      const formValues = {
        ...submittedValues,
        metadata: {
          ...submittedValues.metadata,
          royaltyOperationsManager: submittedValues.metadata.royaltyOperationsManager.oid,
          royaltyAnalyst: submittedValues.metadata.royaltyAnalyst.oid,
          businessAccountManager: submittedValues.metadata.businessAccountManager.oid,
          businessProcessingManager: submittedValues.metadata.businessProcessingManager.oid,
          contractProcessingManager: submittedValues.metadata.contractProcessingManager.oid,
        },
      };
      if (address) {
        response = await dispatch(editContract(address, formValues));
      } else {
        response = await dispatch(createContract(formValues));
      }

      if (response.error && response.payload.message) {
        const error = new Error(response.payload.message);
        error.payload = response.payload.message;
        setErrors({ form: error.message });
        await onFailure(response);
        return response;
      }

      response = get(response, `payload.entities.contracts.${get(response, 'payload.result')}`, {});
      await onSuccess(response);

      return response;
    };

    const submitHandler = async (formikValues, { setErrors }) => {
      const submittedValues = {
        ...formikValues,
        effectiveDate: moment.utc(formikValues.effectiveDate).startOf('day').toISOString(),
        terminationDate: moment.utc(formikValues.terminationDate).startOf('day').toISOString(),
        metadata: {
          ...formikValues.metadata,
          firstStatementPeriod: moment
            .utc(formikValues.metadata.firstStatementPeriod)
            .endOf('month')
            .toISOString(),
        },
      };
      if (formikValues.pendingUpdates?.contractCurrencyEffectiveDate) {
        const ccEffectiveDate = moment(
          formikValues.pendingUpdates.contractCurrencyEffectiveDate,
        ).format('MM/DD/YYYY');
        submittedValues.pendingUpdates.contractCurrencyEffectiveDate = moment
          .utc(ccEffectiveDate)
          .startOf('day')
          .toISOString();
      }
      if (formikValues.pendingUpdates?.paymentCurrencyEffectiveDate) {
        const pcEffectiveDate = moment(
          formikValues.pendingUpdates.paymentCurrencyEffectiveDate,
        ).format('MM/DD/YYYY');
        submittedValues.pendingUpdates.paymentCurrencyEffectiveDate = moment
          .utc(pcEffectiveDate)
          .startOf('day')
          .toISOString();
      }

      if (
        submittedValues.pendingUpdates?.contractCurrency !==
          submittedValues.pendingUpdates?.paymentCurrency ||
        submittedValues.pendingUpdates?.contractCurrencyEffectiveDate !==
          submittedValues.pendingUpdates?.paymentCurrencyEffectiveDate
      ) {
        setErrors({
          form: 'Contract and payment currency error. Please ensure that the contract and payment currencies and the effective dates of the changes match.',
        });
        return;
      }

      if (contract?.approvalStatus?.toLowerCase() === 'approved' && critTouched) {
        showDialog();
        setFormData(submittedValues);
      } else {
        submitToApi(submittedValues, setErrors);
      }
    };

    const isCriticalChange = touchedFields =>
      criticalFields.some(critical => get(touchedFields, critical));

    return (
      <Formik
        innerRef={ref}
        initialValues={initialValues}
        enableReinitialize
        validationSchema={validationSchema}
        validateOnChange={false}
        validateOnBlur={false}
        onSubmit={onSubmit || submitHandler}
      >
        {({
          values,
          errors,
          isSubmitting,
          setFieldValue,
          setFieldTouched,
          setErrors,
          handleBlur,
          touched,
        }) => {
          // only check if critTouched is not true - after that we can short circuit.
          if (!critTouched && !isSubmitting) setCritTouched(isCriticalChange(touched));
          let quarterStartMonths;

          if (
            address &&
            values.metadata?.statementFrequency === 'quarter' &&
            values.metadata?.paymentStartDate
          ) {
            quarterStartMonths = getQuarterStartMonths(values.metadata.paymentStartDate);
          }

          const getCurrencyField = name => {
            let currencyField;
            let effectiveDateField;
            switch (name) {
              case 'contractCurrency':
                currencyField = `pendingUpdates.${name}`;
                effectiveDateField = `pendingUpdates.contractCurrencyEffectiveDate`;
                return { currencyField, effectiveDateField };
              case 'pendingUpdates.contractCurrency':
                currencyField = name;
                effectiveDateField = `${name}EffectiveDate`;
                return { currencyField, effectiveDateField };
              case 'metadata.paymentCurrency':
                currencyField = `pendingUpdates.paymentCurrency`;
                effectiveDateField = `pendingUpdates.paymentCurrencyEffectiveDate`;
                return { currencyField, effectiveDateField };
              case 'pendingUpdates.paymentCurrency':
                currencyField = name;
                effectiveDateField = `${name}EffectiveDate`;
                return { currencyField, effectiveDateField };
              default:
                return '';
            }
          };

          // update effective date for currency if currency changed
          const handleCurrencyChange = (
            formValues,
            errorData,
            setValue,
            setTouched,
            currency,
            name,
          ) => {
            const { currencyField, effectiveDateField } = getCurrencyField(name);
            if (address && currency.key !== get(initialValues, name, '')) {
              setShowDateSelectionDialog(true);
              setCurrencyData({ name: currencyField, value: currency.key });
              setValue(name, get(formValues, name, ''));
              setCurrentCurrencyDate(get(formValues, effectiveDateField, ''));
              setTempCurrency(getCurrencyStateValue(currencyField));
              setCurrency(currency, findCurrencyState(currencyField));
            } else {
              if (get(values, effectiveDateField, null)) setValue(effectiveDateField, '');
              setCurrencyData({});
            }
          };

          const updateCurrencyData = () => {
            const currencyUpdated = { ...currencyData };
            const fieldName = `${currencyUpdated.name}EffectiveDate`;
            setFieldValue(currencyUpdated.name, currencyUpdated.value);
            setFieldValue(fieldName, currentCurrencyDate);
            setFieldTouched(currencyUpdated.name);
            setCurrencyData({});
            setTempCurrency({});
          };

          const revertCurrencyData = () => {
            setCurrency(tempCurrency, findCurrencyState(currencyData.name));
            setTempCurrency({});
          };

          const currencyCallout = currencyType => {
            const effectiveDate =
              values?.pendingUpdates && values.pendingUpdates[`${currencyType}EffectiveDate`];

            if (!address || !effectiveDate) {
              return '';
            }

            const isCurrencyChanged =
              contract?.pendingUpdates && contract?.pendingUpdates[currencyType];
            const isCurrencyApproved =
              contract?.approvalStatus?.toLowerCase() === 'approved' || !isCurrencyChanged;

            if (new Date(effectiveDate) <= new Date() && isCurrencyApproved) {
              return '';
            }

            return (
              <>
                {startCase(currencyType)} change effective date:{' '}
                <strong>{moment(effectiveDate).format('MM/DD/YYYY')}</strong>
              </>
            );
          };

          const contractCurrencyCallout = currencyCallout('contractCurrency');
          const paymentCurrencyCallout = currencyCallout('paymentCurrency');
          const currencyField = type => {
            if (values?.pendingUpdates && values.pendingUpdates[type]) {
              return `pendingUpdates.${type}`;
            }
            return type === 'paymentCurrency' ? 'metadata.paymentCurrency' : type;
          };

          return (
            <div>
              <Form>
                {errors.form && (
                  <MessageBar
                    messageBarType={MessageBarType.error}
                    messageBarIconProps={{ iconName: 'Info' }}
                    isMultiline
                    dismissButtonAriaLabel="Close"
                    nowrap
                  >
                    {errors.form}
                  </MessageBar>
                )}

                <SubModule disableAccordian>
                  <SubModuleHeader variant="large">General Information</SubModuleHeader>
                  <SubModuleContent>
                    <StackGrid smCols={1} mdCols={2} lgCols={3} shrink>
                      <Field
                        name="silo"
                        label="Contract Silo"
                        options={silos}
                        component={Dropdown}
                        disabled={address !== null || reviewMode || isSubmitting}
                        required
                      />
                      <Field
                        name="partnerAddress"
                        label="Partner Name"
                        initialValue={selectedPartner}
                        component={PartnerTypeAhead}
                        disabled={address !== null || reviewMode || isSubmitting}
                        required
                      />
                      <Field
                        name="name"
                        label="Contract Name"
                        component={TextField}
                        required
                        disabled={reviewMode || isSubmitting}
                      />
                      <Field
                        name="contractId"
                        label="Contract ID"
                        component={TextField}
                        required
                        disabled={reviewMode || isSubmitting}
                      />
                      <Field
                        name="effectiveDate"
                        label="Effective Date"
                        component={DatePicker}
                        required
                        disabled={reviewMode || isSubmitting}
                        onBlur={handleBlur}
                        {...createDatePickerProps(null, values.terminationDate)}
                      />
                      <Field
                        name="terminationDate"
                        label="Expiration Date"
                        component={DatePicker}
                        required
                        disabled={reviewMode || isSubmitting}
                        onBlur={handleBlur}
                        {...createDatePickerProps(values.effectiveDate, null)}
                      />
                      <Field
                        name={currencyField('contractCurrency')}
                        component={CurrencyTypeAhead}
                        selectedValue={contractCurrency}
                        label="Contract Currency"
                        handleCallback={handleCurrencyChange}
                        callout={contractCurrencyCallout}
                        required
                        className={styles.noBg}
                        dropdownLabelProps={{ style: { display: 'none' } }}
                        disabled={reviewMode || isSubmitting}
                        onBlur={handleBlur}
                      />
                      <Field
                        name="perCapDays"
                        label="Per Cap Days"
                        component={TextField}
                        type="number"
                        min={0}
                        disabled={
                          values.silo.toLowerCase() === '1p-azure' ||
                          reviewMode ||
                          isSubmitting ||
                          (!window.config.enablePercapTiering && hasTiering)
                        }
                      />
                      <Field
                        name="perCapPercentage"
                        label="Per Cap Percentage"
                        component={TextField}
                        type="number"
                        min={1}
                        max={100}
                        suffix="%"
                        disabled={
                          values.silo.toLowerCase() === '1p-azure' ||
                          reviewMode ||
                          isSubmitting ||
                          (!window.config.enablePercapTiering && hasTiering)
                        }
                      />
                      <Field
                        name="metadata.statementFrequency"
                        label="Statement Frequency"
                        component={Dropdown}
                        options={statementFrequency}
                        required
                        disabled={reviewMode || isSubmitting}
                      />
                      <Field
                        name="metadata.firstStatementPeriod"
                        label="First Statement Period"
                        component={DatePicker}
                        disabled={reviewMode || isSubmitting}
                        {...createDatePickerProps(
                          values.effectiveDate,
                          values.terminationDate,
                          'monthly',
                        )}
                      />
                      <Field
                        name="metadata.auditWindow"
                        label="Audit Window"
                        component={TextField}
                        type="number"
                        min={0}
                        suffix="days"
                        required
                        disabled={reviewMode || isSubmitting}
                      />

                      <Field
                        name="metadata.vendorId"
                        label="Vendor Id"
                        component={TextField}
                        type="string"
                        required
                        disabled={reviewMode || isSubmitting}
                      />
                    </StackGrid>
                  </SubModuleContent>
                </SubModule>

                <SubModule disableAccordian>
                  <SubModuleHeader variant="large">Accounting and Payment Details</SubModuleHeader>
                  <SubModuleContent>
                    <StackGrid smCols={1} mdCols={2} lgCols={3} shrink>
                      <Field
                        name="metadata.companyCode"
                        label="Company Code"
                        options={companyCodes}
                        component={Dropdown}
                        disabled={reviewMode || isSubmitting}
                      />
                      <Field
                        name="metadata.taxCode"
                        label="Tax Code"
                        options={taxCodes}
                        component={Dropdown}
                        disabled={reviewMode || isSubmitting}
                      />
                      <Field
                        name="metadata.purchaseClassificationCode"
                        label="Purchase Classification Code"
                        options={purchaseClassificationCodes}
                        component={Dropdown}
                        disabled={reviewMode || isSubmitting}
                      />
                      <Field
                        name="metadata.costCenter"
                        label="Cost Center/Internal Order"
                        component={TextField}
                        disabled={reviewMode || isSubmitting}
                      />
                      <Field
                        name="metadata.expenseAccount"
                        label="Expense Account"
                        component={TextField}
                        disabled={reviewMode || isSubmitting}
                      />
                      <Field
                        name="metadata.liabilityAccount"
                        label="Liability Account"
                        component={TextField}
                        disabled={reviewMode || isSubmitting}
                      />
                      <Field
                        name={currencyField('paymentCurrency')}
                        component={CurrencyTypeAhead}
                        selectedValue={paymentCurrency}
                        label="Payment Currency"
                        handleCallback={handleCurrencyChange}
                        callout={paymentCurrencyCallout}
                        required
                        className={styles.noBg}
                        dropdownLabelProps={{ style: { display: 'none' } }}
                        disabled={reviewMode || isSubmitting}
                        onBlur={handleBlur}
                      />
                      <Field
                        name="metadata.paymentTerms"
                        label="Payment Terms"
                        component={TextField}
                        type="number"
                        min={0}
                        suffix="days"
                        required
                        disabled={reviewMode || isSubmitting}
                      />
                      <Field
                        name="metadata.paymentStartDate"
                        label="Payment Start Date"
                        component={DatePicker}
                        required
                        disabled={reviewMode || isSubmitting}
                        onBlur={handleBlur}
                      />
                      <Field
                        name="metadata.paymentMinimum"
                        label="Payment Minimum"
                        component={TextField}
                        type="number"
                        min={0}
                        required
                        disabled={reviewMode || isSubmitting}
                      />
                      <Field
                        name="metadata.productFamily"
                        label="Product Family"
                        component={TextField}
                        disabled={reviewMode || isSubmitting}
                      />
                    </StackGrid>
                  </SubModuleContent>
                </SubModule>

                <SubModule disableAccordian>
                  <SubModuleHeader variant="large">Additional Information</SubModuleHeader>
                  <SubModuleContent>
                    <StackGrid smCols={1} mdCols={2} lgCols={3}>
                      <Field
                        name="metadata.comment"
                        label="Comments"
                        multiline
                        component={TextField}
                        placeholder="Add comments"
                        disabled={reviewMode || isSubmitting}
                      />
                      <Field
                        name="metadata.keywords"
                        label="Keywords"
                        multiline
                        component={TextField}
                        placeholder="Add keywords separated by commas"
                        disabled={reviewMode || isSubmitting}
                      />
                    </StackGrid>
                  </SubModuleContent>
                </SubModule>

                <SubModule disableAccordian>
                  <SubModuleHeader variant="large">Contract Assignments</SubModuleHeader>
                  <SubModuleContent>
                    <StackGrid smCols={1} mdCols={2} lgCols={3} shrink>
                      <Field
                        name="metadata.businessAccountManager.oid"
                        label="Business Account Manager"
                        component={Dropdown}
                        options={businessAccountManagers}
                        required
                        disabled={reviewMode || isSubmitting}
                      />
                      <Field
                        name="metadata.businessProcessingManager.oid"
                        label="Business Processing Manager"
                        component={Dropdown}
                        options={businessProcessingManagers}
                        required
                        disabled={reviewMode || isSubmitting}
                      />
                      <Field
                        name="metadata.royaltyOperationsManager.oid"
                        label="Royalty Operations Manager"
                        component={Dropdown}
                        options={royaltyOperationsManagers}
                        required
                        disabled={reviewMode || isSubmitting}
                      />
                      <Field
                        name="metadata.royaltyAnalyst.oid"
                        label="Royalty Analyst"
                        component={Dropdown}
                        options={royaltyAnalysts}
                        required
                        disabled={reviewMode || isSubmitting}
                      />
                      <Field
                        name="metadata.contractProcessingManager.oid"
                        label="Contract Processing Manager"
                        component={Dropdown}
                        options={contractProcessingManagers}
                        required
                        disabled={reviewMode || isSubmitting}
                      />
                    </StackGrid>
                  </SubModuleContent>
                </SubModule>
              </Form>
              <Dialog hidden={dialogHidden} dialogContentProps={dialogContentProps}>
                <DialogFooter>
                  <PrimaryButton
                    text="Continue"
                    onClick={() => {
                      hideDialog();
                      submitToApi(formData, setErrors).catch(e => setErrors({ form: e }));
                    }}
                  />
                  <DefaultButton
                    text="Cancel"
                    onClick={async () => {
                      hideDialog();
                      await onFailure(); // This sets submitting to false, which hides the submit loader
                    }}
                  />
                </DialogFooter>
              </Dialog>
              {showDateSelectionDialog && (
                <ContractDateSelectionDialog
                  isOpen={showDateSelectionDialog}
                  currencyType={currencyData?.name}
                  value={currentCurrencyDate}
                  statementType={values?.metadata?.statementFrequency}
                  onDismiss={() => {
                    setShowDateSelectionDialog(false);
                    revertCurrencyData();
                  }}
                  onConfirm={() => {
                    setShowDateSelectionDialog(false);
                    updateCurrencyData();
                  }}
                  setEffectiveDate={setCurrentCurrencyDate}
                  quarterStartMonths={quarterStartMonths}
                />
              )}
            </div>
          );
        }}
      </Formik>
    );
  },
);

ContractTermsForm.propTypes = {
  address: PropTypes.string,
  onSubmit: PropTypes.func,
  onSuccess: PropTypes.func,
  onFailure: PropTypes.func,
  reviewMode: PropTypes.bool,
};

ContractTermsForm.defaultProps = {
  address: null,
  onSubmit: null,
  onSuccess: () => {},
  onFailure: () => {},
  /**
   * When true, the form fields will all be disabled and nothing will be editable.
   */
  reviewMode: false,
};

export default ContractTermsForm;
